import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { BreadCrumb, LineCharts, TableContainer } from "@/components";
import { PATH_NAME, StatusOptions } from "@/constants";
import {
  getCampaignResult,
  getTopSubscribersByProfit,
  getTopSubscribersBySales,
  getTotalAgencyClients,
  getTotalAgencySubscribers,
  getTotalCampaignProfitFromSubscribers,
  getTotalCampaignSalesFromSubscribers,
  getTotalCampaignsFromSubscribers,
  getTotalDIYPaidSubscribers,
  getTotalFreeTrialSubscribers,
  getTotalInactiveSubscribers,
  getTotalIntegrationSubscribers,
  getTotalNewSubscribers,
  getTotalPaidSubscribers,
  getTotalTerminatedSubscribers,
} from "@/features";
import { CampaignType, ChartProps, OptionProps } from "@/types";
import { formatCurrency } from "@/utils";
import { ColumnDef } from "@tanstack/react-table";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import Flatpickr from "react-flatpickr";
import Select from "react-select";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Row,
} from "reactstrap";
import { exportColumns } from "./constants";
import { FilterProps } from "./types";

const Dashboard = () => {
  const { campaignResultList } = useAppSelector((state) => state.dashboard);
  const dispatch = useAppDispatch();
  const [data, setData] = useState<CampaignType[]>([]);
  const [campaignTypeOptions, setCampaignTypeOptions] = useState<
    OptionProps[] | null
  >(null);
  const [filter, setFilter] = useState<FilterProps>({
    campaign_name: "",
    campaign_type: null,
    fromDate: "",
    toDate: "",
    status: null,
  });
  const [isLoading, setIsLoading] = useState(false);

  const [topSubscribersBySales, setTopSubscribersBySales] =
    useState<ChartProps>({
      data: [],
      isLoading: true,
    });
  const [topSubscribersByProfit, setTopSubscribersByProfit] =
    useState<ChartProps>({
      data: [],
      isLoading: true,
    });
  const [totalNewSubscribers, setTotalNewSubscribers] = useState<ChartProps>({
    data: [],
    isLoading: true,
  });
  const [totalTerminatedSubscribers, setTotalTerminatedSubscribers] =
    useState<ChartProps>({
      data: [],
      isLoading: true,
    });
  const [totalFreeTrialSubscribers, setTotalFreeTrialSubscribers] =
    useState<ChartProps>({
      data: [],
      isLoading: true,
    });
  const [totalPaidSubscribers, setTotalPaidSubscribers] = useState<ChartProps>({
    data: [],
    isLoading: true,
  });
  const [totalDIYPaidSubscribers, setTotalDIYPaidSubscribers] =
    useState<ChartProps>({
      data: [],
      isLoading: true,
    });
  const [totalIntegrationSubscribers, setTotalIntegrationSubscribers] =
    useState<ChartProps>({
      data: [],
      isLoading: true,
    });
  const [totalAgencySubscribers, setTotalAgencySubscribers] =
    useState<ChartProps>({
      data: [],
      isLoading: true,
    });
  const [totalAgencyClients, setTotalAgencyClients] = useState<ChartProps>({
    data: [],
    isLoading: true,
  });
  const [totalInactiveSubscribers, setTotalInactiveSubscribers] =
    useState<ChartProps>({
      data: [],
      isLoading: true,
    });
  const [totalCampaignsFromSubscribers, setTotalCampaignsFromSubscribers] =
    useState<ChartProps>({
      data: [],
      isLoading: true,
    });
  const [
    totalCampaignSalesFromSubscribers,
    setTotalCampaignSalesFromSubscribers,
  ] = useState<ChartProps>({
    data: [],
    isLoading: true,
  });
  const [
    totalCampaignProfitFromSubscribers,
    setTotalCampaignProfitFromSubscribers,
  ] = useState<ChartProps>({
    data: [],
    isLoading: true,
  });

  const columns = useMemo<ColumnDef<CampaignType>[]>(
    () => [
      {
        header: "Name",
        accessorKey: "name",
        disableFilters: true,
        enableColumnFilter: false,
      },
      {
        header: "Type",
        accessorKey: "campaign_type_name",
        disableFilters: true,
        enableColumnFilter: false,
        cell: ({ row }) => row.original.campaign_type_name,
      },
      {
        header: "Start Date",
        accessorKey: "start_date",
        disableFilters: true,
        enableColumnFilter: false,
        cell: ({ row }) => moment(row.original.start_date).format("DD/MM/YYYY"),
      },
      {
        header: "End Date",
        accessorKey: "end_date",
        disableFilters: true,
        enableColumnFilter: false,
        cell: ({ row }) => moment(row.original.end_date).format("DD/MM/YYYY"),
      },
      {
        header: "Total Customer",
        accessorKey: "total_customer",
        disableFilters: true,
        enableColumnFilter: false,
        headerClass: "text-center",
        rowClass: "text-center",
        cell: ({ row }) => Number(row.original.total_customer),
      },
      {
        header: "Total Sales",
        accessorKey: "total_sale",
        disableFilters: true,
        enableColumnFilter: false,
        cell: ({ row }) => formatCurrency(row.original.total_sale),
      },
      {
        header: "Total Cost",
        accessorKey: "total_cost",
        disableFilters: true,
        enableColumnFilter: false,
        cell: ({ row }) => formatCurrency(row.original.total_cost),
      },
      {
        header: "Average Sales per Customer",
        accessorKey: "average_spend_per_customer",
        disableFilters: true,
        enableColumnFilter: false,
        cell: ({ row }) =>
          formatCurrency(row.original.average_spend_per_customer),
      },
      {
        header: "Profit per Customer",
        accessorKey: "profit_per_customer",
        disableFilters: true,
        enableColumnFilter: false,
        sortDescFirst: false,
        cell: ({ row }) => formatCurrency(row.original.profit_per_customer),
      },
    ],
    [],
  );

  const onExportPDFDashBoard = () => {
    window.open(PATH_NAME.REPORT_DASHBOARD);
  };

  const convertArrayOfObjectsToCSV = (array: any) => {
    let result = "";

    const columnDelimiter = ",";
    const lineDelimiter = "\n";
    result += exportColumns.map((d) => d.label).join(columnDelimiter);
    result += lineDelimiter;

    array.forEach((item: any) => {
      let ctr = 0;
      exportColumns.forEach((column) => {
        if (ctr > 0) result += columnDelimiter;

        let key = column.value;
        let cellValue = "";

        let keySplits = key.split(".");
        keySplits.forEach((subKey: any) => {
          cellValue = cellValue ? cellValue[subKey] : item[subKey];
        });

        result += '"' + cellValue + '"';
        ctr++;
      });
      result += lineDelimiter;
    });
    return result;
  };

  const onExportCSVDashboard = () => {
    let csv = convertArrayOfObjectsToCSV(campaignResultList);
    if (csv == null) return;

    const filename = "Export_Dashboard.csv";

    if (!csv.match(/^data:text\/csv/i)) {
      csv = `data:text/csv;charset=utf-8,${csv}`;
    }

    let link = document.createElement("a");
    link.setAttribute("href", encodeURI(csv));
    link.setAttribute("download", filename);
    link.click();
  };

  const onExportPDFCampaignResult = () => {
    window.open(PATH_NAME.REPORT_CAMPAIGN_RESULT);
  };

  const onExportCSVCampaignResult = () => {
    let csv = convertArrayOfObjectsToCSV(campaignResultList);
    if (csv == null) return;

    const filename = "Export_Campaign_Result.csv";

    if (!csv.match(/^data:text\/csv/i)) {
      csv = `data:text/csv;charset=utf-8,${csv}`;
    }

    let link = document.createElement("a");
    link.setAttribute("href", encodeURI(csv));
    link.setAttribute("download", filename);
    link.click();
  };

  const onApply = () => {
    let filteredData = campaignResultList;

    if (filter.campaign_name?.trim()) {
      filteredData = filteredData.filter((item: CampaignType) =>
        item.name
          .toLowerCase()
          .includes(filter.campaign_name.toLowerCase()?.trim()),
      );
    }

    if (filter.campaign_type) {
      filteredData = filteredData.filter(
        (item: CampaignType) =>
          item.campaign_type_name?.toLowerCase() ===
          filter.campaign_type?.value?.toLowerCase(),
      );
    }

    if (filter.status) {
      filteredData = filteredData.filter(
        (item: CampaignType) =>
          item.status.toLowerCase() === filter.status?.value?.toLowerCase(),
      );
    }

    if (filter.fromDate) {
      filteredData = filteredData.filter((data: CampaignType) =>
        moment(filter.fromDate).isSameOrBefore(moment(data.start_date)),
      );
    }

    if (filter.toDate) {
      filteredData = filteredData.filter((data: CampaignType) =>
        moment(filter.toDate, "YYYY-MM-DD").isSameOrAfter(
          moment(data.end_date),
        ),
      );
    }

    setData(filteredData);
  };

  const onReset = () => {
    setFilter({
      campaign_name: "",
      campaign_type: null,
      fromDate: "",
      toDate: "",
      status: null,
    });
    setData(campaignResultList);
  };

  const getData = async () => {
    setIsLoading(true);
    try {
      const data = await dispatch(getCampaignResult()).unwrap();
      setData(data);
      let types: OptionProps[] = [];
      data.forEach((item) => {
        if (!types.find((s) => s?.value === item.campaign_type_name)) {
          types.push({
            value: item.campaign_type_name,
            label: item.campaign_type_name,
          });
        }
      });
      setCampaignTypeOptions(types);
    } catch (error) {
      console.error("error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadFirstChart = useCallback(async () => {
    try {
      const data = await dispatch(getTopSubscribersBySales()).unwrap();
      setTopSubscribersBySales({
        data,
        isLoading: false,
      });
    } catch (error) {
      console.log("error: ", error);
    }
  }, [dispatch]);

  const loadSecondChart = useCallback(async () => {
    try {
      const data = await dispatch(getTopSubscribersByProfit()).unwrap();
      setTopSubscribersByProfit({
        data,
        isLoading: false,
      });
    } catch (error) {
      console.log("error: ", error);
    }
  }, [dispatch]);

  const loadthirdChart = useCallback(async () => {
    try {
      const data = await dispatch(getTotalNewSubscribers()).unwrap();
      setTotalNewSubscribers({
        data,
        isLoading: false,
      });
    } catch (error) {
      console.log("error: ", error);
    }
  }, [dispatch]);

  const loadFourthChart = useCallback(async () => {
    try {
      const data = await dispatch(getTotalTerminatedSubscribers()).unwrap();
      setTotalTerminatedSubscribers({
        data,
        isLoading: false,
      });
    } catch (error) {
      console.log("error: ", error);
    }
  }, [dispatch]);

  const loadFifthChart = useCallback(async () => {
    try {
      const data = await dispatch(getTotalFreeTrialSubscribers()).unwrap();
      setTotalFreeTrialSubscribers({
        data,
        isLoading: false,
      });
    } catch (error) {
      console.log("error: ", error);
    }
  }, [dispatch]);

  const loadSixthChart = useCallback(async () => {
    try {
      const data = await dispatch(getTotalPaidSubscribers()).unwrap();
      setTotalPaidSubscribers({
        data,
        isLoading: false,
      });
    } catch (error) {
      console.log("error: ", error);
    }
  }, [dispatch]);

  const loadSeventhChart = useCallback(async () => {
    try {
      const data = await dispatch(getTotalDIYPaidSubscribers()).unwrap();
      setTotalDIYPaidSubscribers({
        data,
        isLoading: false,
      });
    } catch (error) {
      console.log("error: ", error);
    }
  }, [dispatch]);

  const loadEighthChart = useCallback(async () => {
    try {
      const data = await dispatch(getTotalIntegrationSubscribers()).unwrap();
      setTotalIntegrationSubscribers({
        data,
        isLoading: false,
      });
    } catch (error) {
      console.log("error: ", error);
    }
  }, [dispatch]);

  const loadNinthChart = useCallback(async () => {
    try {
      const data = await dispatch(getTotalAgencySubscribers()).unwrap();
      setTotalAgencySubscribers({
        data,
        isLoading: false,
      });
    } catch (error) {
      console.log("error: ", error);
    }
  }, [dispatch]);

  const loadTenthChart = useCallback(async () => {
    try {
      const data = await dispatch(getTotalAgencyClients()).unwrap();
      setTotalAgencyClients({
        data,
        isLoading: false,
      });
    } catch (error) {
      console.log("error: ", error);
    }
  }, [dispatch]);

  const loadEleventhChart = useCallback(async () => {
    try {
      const data = await dispatch(getTotalInactiveSubscribers()).unwrap();
      setTotalInactiveSubscribers({
        data,
        isLoading: false,
      });
    } catch (error) {
      console.log("error: ", error);
    }
  }, [dispatch]);

  const loadTwelfthChart = useCallback(async () => {
    try {
      const data = await dispatch(getTotalCampaignsFromSubscribers()).unwrap();
      setTotalCampaignsFromSubscribers({
        data,
        isLoading: false,
      });
    } catch (error) {
      console.log("error: ", error);
    }
  }, [dispatch]);

  const loadThirteenthChart = useCallback(async () => {
    try {
      const data = await dispatch(
        getTotalCampaignSalesFromSubscribers(),
      ).unwrap();
      setTotalCampaignSalesFromSubscribers({
        data,
        isLoading: false,
      });
    } catch (error) {
      console.log("error: ", error);
    }
  }, [dispatch]);

  const loadFourteenthChart = useCallback(async () => {
    try {
      const data = await dispatch(
        getTotalCampaignProfitFromSubscribers(),
      ).unwrap();
      setTotalCampaignProfitFromSubscribers({
        data,
        isLoading: false,
      });
    } catch (error) {
      console.log("error: ", error);
    }
  }, [dispatch]);

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadFirstChart();
    loadSecondChart();
    loadthirdChart();
    loadFourthChart();
    loadFifthChart();
    loadSixthChart();
    loadSeventhChart();
    loadEighthChart();
    loadNinthChart();
    loadTenthChart();
    loadEleventhChart();
    loadTwelfthChart();
    loadThirteenthChart();
    loadFourteenthChart();
  }, [
    loadEighthChart,
    loadEleventhChart,
    loadFifthChart,
    loadFirstChart,
    loadFourteenthChart,
    loadFourthChart,
    loadNinthChart,
    loadSecondChart,
    loadSeventhChart,
    loadSixthChart,
    loadTenthChart,
    loadTwelfthChart,
    loadThirteenthChart,
    loadthirdChart,
  ]);

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Dashboard" />
        <Card>
          <CardBody>
            <div className="d-flex align-items-center justify-content-end gap-2">
              <FormGroup>
                <Button color="primary" onClick={onExportPDFDashBoard}>
                  <i className="ri-file-pdf-line me-2" />
                  Export PDF
                </Button>
              </FormGroup>
              <FormGroup onClick={onExportCSVDashboard}>
                <Button color="primary">
                  <i className="ri-file-excel-2-line me-2" />
                  Export CSV
                </Button>
              </FormGroup>
            </div>
            <Row>
              <Col xs={12} lg={4}>
                <FormGroup>
                  <Label>Campaign Name</Label>
                  <div className="form-icon right">
                    <Input
                      placeholder="Search by campaign name"
                      value={filter.campaign_name}
                      onChange={(e) =>
                        setFilter({
                          ...filter,
                          campaign_name: e.target.value,
                        })
                      }
                    />
                    <i className="ri-search-line" />
                  </div>
                </FormGroup>
              </Col>
              <Col xs={12} lg={4}>
                <FormGroup>
                  <Label>Campaign Type</Label>
                  <Select
                    options={campaignTypeOptions}
                    value={filter.campaign_type}
                    placeholder="Select campaign type"
                    onChange={(item: OptionProps) => {
                      setFilter({
                        ...filter,
                        campaign_type: item,
                      });
                    }}
                  />
                </FormGroup>
              </Col>
              <Col xs={12} lg={4}>
                <FormGroup>
                  <Label>Status</Label>
                  <Select
                    options={StatusOptions}
                    value={filter.status}
                    placeholder="Select status"
                    onChange={(item: OptionProps) => {
                      setFilter({
                        ...filter,
                        status: item,
                      });
                    }}
                  />
                </FormGroup>
              </Col>
              <Col xs={12} lg={4}>
                <FormGroup>
                  <Label>From Date</Label>
                  <Flatpickr
                    className="form-control"
                    options={{
                      dateFormat: "d/m/Y",
                    }}
                    placeholder="From Date"
                    value={filter.fromDate}
                    onChange={(_, date) => {
                      console.log("from date", date);
                      setFilter({
                        ...filter,
                        fromDate: date,
                      });
                    }}
                  />
                </FormGroup>
              </Col>
              <Col xs={12} lg={4}>
                <FormGroup>
                  <Label>To Date</Label>
                  <Flatpickr
                    className="form-control"
                    options={{
                      dateFormat: "d/m/Y",
                    }}
                    placeholder="To Date"
                    value={filter.toDate}
                    onChange={(_, date) => {
                      console.log("from date", date);
                      setFilter({
                        ...filter,
                        toDate: date,
                      });
                    }}
                  />
                </FormGroup>
              </Col>
              <Col xs={12} lg={3} className="mt-auto">
                <FormGroup>
                  <Button color="primary" onClick={onApply}>
                    Apply
                  </Button>
                  <Button color="light" className="ms-2" onClick={onReset}>
                    Reset
                  </Button>
                </FormGroup>
              </Col>
            </Row>
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <Row className="align-items-center">
              <Col xs={12} md={6}>
                <h4 className="card-title mb-0 flex-grow-1">
                  Campaign Result Summary
                </h4>
              </Col>
              <Col xs={12} md={6}>
                <div className="d-flex align-items-center justify-content-end gap-2">
                  <FormGroup className="custom-form-group">
                    <Button color="primary" onClick={onExportPDFCampaignResult}>
                      <i className="ri-file-pdf-line me-2" />
                      Export PDF
                    </Button>
                  </FormGroup>
                  <FormGroup
                    className="custom-form-group"
                    onClick={onExportCSVCampaignResult}
                  >
                    <Button color="primary">
                      <i className="ri-file-excel-2-line me-2" />
                      Export CSV
                    </Button>
                  </FormGroup>
                </div>
              </Col>
            </Row>
          </CardHeader>
          <CardBody>
            <TableContainer
              columns={columns || []}
              data={data || []}
              customPageSize={10}
              isLoading={isLoading}
              tableClass="table-centered align-middle table-nowrap mb-0"
              theadClass="text-muted table-light"
              divClass="table-responsive"
            />
          </CardBody>
        </Card>

        <Row>
          <Col lg={6}>
            <Card>
              <CardHeader>
                <h4 className="card-title mb-0">Top Subscribers by Sales</h4>
              </CardHeader>
              <CardBody>
                <LineCharts
                  data={topSubscribersBySales.data}
                  isLoading={topSubscribersBySales.isLoading}
                />
              </CardBody>
            </Card>
          </Col>
          <Col lg={6}>
            <Card>
              <CardHeader>
                <h4 className="card-title mb-0">Top Subscribers by Profit</h4>
              </CardHeader>
              <CardBody>
                <LineCharts
                  data={topSubscribersByProfit.data}
                  isLoading={topSubscribersByProfit.isLoading}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col lg={6}>
            <Card>
              <CardHeader>
                <h4 className="card-title mb-0">
                  Total New Subscribers per Month (YTD)
                </h4>
              </CardHeader>
              <CardBody>
                <LineCharts
                  data={totalNewSubscribers.data}
                  isLoading={totalNewSubscribers.isLoading}
                />
              </CardBody>
            </Card>
          </Col>
          <Col lg={6}>
            <Card>
              <CardHeader>
                <h4 className="card-title mb-0">
                  Total Terminated Subscribers per Month (YTD)
                </h4>
              </CardHeader>
              <CardBody>
                <LineCharts
                  data={totalTerminatedSubscribers.data}
                  isLoading={totalTerminatedSubscribers.isLoading}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col lg={6}>
            <Card>
              <CardHeader>
                <h4 className="card-title mb-0">
                  Total Free Trial Subscribers per Month (YTD)
                </h4>
              </CardHeader>
              <CardBody>
                <LineCharts
                  data={totalFreeTrialSubscribers.data}
                  isLoading={totalFreeTrialSubscribers.isLoading}
                />
              </CardBody>
            </Card>
          </Col>
          <Col lg={6}>
            <Card>
              <CardHeader>
                <h4 className="card-title mb-0">
                  Total Paid Subscribers per Month (YTD)
                </h4>
              </CardHeader>
              <CardBody>
                <LineCharts
                  data={totalPaidSubscribers.data}
                  isLoading={totalPaidSubscribers.isLoading}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col lg={6}>
            <Card>
              <CardHeader>
                <h4 className="card-title mb-0">
                  Total DIY Paid Subscribers per Month (YTD)
                </h4>
              </CardHeader>
              <CardBody>
                <LineCharts
                  data={totalDIYPaidSubscribers.data}
                  isLoading={totalDIYPaidSubscribers.isLoading}
                />
              </CardBody>
            </Card>
          </Col>
          <Col lg={6}>
            <Card>
              <CardHeader>
                <h4 className="card-title mb-0">
                  Total Integration Subscribers per Month (YTD)
                </h4>
              </CardHeader>
              <CardBody>
                <LineCharts
                  data={totalIntegrationSubscribers.data}
                  isLoading={totalIntegrationSubscribers.isLoading}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col lg={6}>
            <Card>
              <CardHeader>
                <h4 className="card-title mb-0">
                  Total Agency Subscribers per Month (YTD)
                </h4>
              </CardHeader>
              <CardBody>
                <LineCharts
                  data={totalAgencySubscribers.data}
                  isLoading={totalAgencySubscribers.isLoading}
                />
              </CardBody>
            </Card>
          </Col>
          <Col lg={6}>
            <Card>
              <CardHeader>
                <h4 className="card-title mb-0">
                  Total Agency Clients per Month (YTD)
                </h4>
              </CardHeader>
              <CardBody>
                <LineCharts
                  data={totalAgencyClients.data}
                  isLoading={totalAgencyClients.isLoading}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col lg={6}>
            <Card>
              <CardHeader>
                <h4 className="card-title mb-0">
                  Total Inactive Subscribers per Month (YTD)
                </h4>
              </CardHeader>
              <CardBody>
                <LineCharts
                  data={totalInactiveSubscribers.data}
                  isLoading={totalInactiveSubscribers.isLoading}
                />
              </CardBody>
            </Card>
          </Col>
          <Col lg={6}>
            <Card>
              <CardHeader>
                <h4 className="card-title mb-0">
                  Total Campaigns from Subscribers per Month (YTD)
                </h4>
              </CardHeader>
              <CardBody>
                <LineCharts
                  data={totalCampaignsFromSubscribers.data}
                  isLoading={totalCampaignsFromSubscribers.isLoading}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col lg={6}>
            <Card>
              <CardHeader>
                <h4 className="card-title mb-0">
                  Total Campaign Sales from Subscribers per Month (YTD)
                </h4>
              </CardHeader>
              <CardBody>
                <LineCharts
                  data={totalCampaignSalesFromSubscribers.data}
                  isLoading={totalCampaignSalesFromSubscribers.isLoading}
                />
              </CardBody>
            </Card>
          </Col>
          <Col lg={6}>
            <Card>
              <CardHeader>
                <h4 className="card-title mb-0">
                  Total Campaign Profit from Subscribers per Month (YTD)
                </h4>
              </CardHeader>
              <CardBody>
                <LineCharts
                  data={totalCampaignProfitFromSubscribers.data}
                  isLoading={totalCampaignProfitFromSubscribers.isLoading}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Dashboard;
